import { useCallback } from "react";

import { useEstateItems } from "@/shared/hooks/useEstateItems";

import type { EssentialModule } from "@/shared/types/estateEssentials";
import type { EstateItem } from "@/shared/types/estateTypes";

import { useSaveUserFields } from "./useSaveUserFields";
import { useSaveBenefitsFields } from "./useSaveBenefitsFields";
import { useSaveCredentials } from "./useSaveCredentials";
import { useSaveTaskAnswers } from "./useSaveTaskAnswers";
import { useSaveVehicleItems } from "./useSaveVehicleItems";

interface UseModuleFormSubmitParams {
  module: EssentialModule | undefined;
  moduleId: string | undefined;
  deceasedUserId: number | undefined;
  authUserId: number | undefined;
  isVehicleModule: boolean;
  vehicleItems: EstateItem[];
  addEstateItem: ReturnType<typeof useEstateItems>["addItem"];
  updateEstateItem: ReturnType<typeof useEstateItems>["updateItem"];
}

export function useModuleFormSubmit({
  module,
  moduleId,
  deceasedUserId,
  authUserId,
  isVehicleModule,
  vehicleItems,
  addEstateItem,
  updateEstateItem,
}: UseModuleFormSubmitParams) {
  const saveUserFields = useSaveUserFields();
  const saveBenefitsFields = useSaveBenefitsFields(moduleId, deceasedUserId);
  const saveCredentials = useSaveCredentials(authUserId, deceasedUserId);
  const saveTaskAnswers = useSaveTaskAnswers(module, isVehicleModule);
  const saveVehicleItems = useSaveVehicleItems(
    isVehicleModule,
    vehicleItems,
    addEstateItem,
    updateEstateItem,
  );

  return useCallback(
    async (data: Record<string, string>): Promise<string | null> => {
      await saveUserFields(data);
      await saveBenefitsFields(data);
      await saveCredentials(data);
      const error = await saveTaskAnswers(data);
      await saveVehicleItems(data);
      return error;
    },
    [saveUserFields, saveBenefitsFields, saveCredentials, saveTaskAnswers, saveVehicleItems],
  );
}
